import * as React from "react";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Box, Chip, Stack, Typography } from "@mui/material";
import styled from "styled-components";

const StatCard = styled(Box)`
    border: 1px solid #585858;
    border-radius: 6px;
    padding: 12px 16px;
    min-width: 120px;
    flex: 1;
`;

const StatLabel = styled.p`
    font-size: 11px;
    margin: 0 0 4px 0;
    color: ${({ theme }) => theme.palette.secondary.main};
    white-space: nowrap;
`;

const StatValue = styled.p`
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    white-space: nowrap;
`;

const StatUnit = styled.span`
    font-size: 13px;
    font-weight: 400;
    margin-left: 4px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const sprintStateColor = (val: string): "success" | "default" | "warning" => {
    const lower = (val ?? "").toLowerCase();
    if (lower === "active") return "success";
    if (lower === "closed") return "default";
    return "warning";
};

const TemplateStats = ({
    data,
    parameters,
}: {
    data: Record<string, unknown>[] | null | undefined;
    parameters: {
        property: string;
        headerKey?: string;
        metaKeys?: { key: string; type?: "chip" | "string"; separator?: string }[];
        items: { label: string; valueKey: string; unit?: string }[];
    };
}) => {
    if (!data || data.length === 0) return <NoDataPlaceholder variant="list" errorMessage={null} />;

    return (
        <Box sx={{ overflow: "auto", width: "100%", height: "100%" }}>
            <Stack direction="column" spacing={1.5} sx={{ height: "100%" }}>
                {data.map((row, i) => {
                    const headerVal =
                        parameters.headerKey != null
                            ? (row[parameters.headerKey] as string | null | undefined)
                            : null;
                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box key={i}>
                            {headerVal && (
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                                    {headerVal}
                                </Typography>
                            )}
                            {parameters.metaKeys && parameters.metaKeys.length > 0 && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    {parameters.metaKeys.map((mk) => {
                                        const val = row[mk.key] as string | null | undefined;
                                        if (val == null) return null;
                                        if (mk.type === "chip") {
                                            return (
                                                <Chip
                                                    key={mk.key}
                                                    label={val}
                                                    size="small"
                                                    color={sprintStateColor(val)}
                                                    variant="outlined"
                                                    sx={{ fontSize: "10px", height: "18px" }}
                                                />
                                            );
                                        }
                                        return (
                                            <Typography
                                                key={mk.key}
                                                sx={{ fontSize: 11, color: "text.secondary" }}
                                            >
                                                {mk.separator ?? ""}
                                                {val}
                                            </Typography>
                                        );
                                    })}
                                </Stack>
                            )}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {parameters.items.map((item) => {
                                    const raw = row[item.valueKey];
                                    const formatted =
                                        raw != null ? Number(raw).toLocaleString() : "—";
                                    return (
                                        <StatCard key={item.valueKey}>
                                            <StatLabel>{item.label}</StatLabel>
                                            <StatValue>
                                                {formatted}
                                                {item.unit && <StatUnit>{item.unit}</StatUnit>}
                                            </StatValue>
                                        </StatCard>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default TemplateStats;
